import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { EmailTemplate } from '@spike/email-marketing-model';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

import { isSenderReadyForSend } from '../../isSenderReadyForSend';
import { EmailCampaignStepProps } from '../../types';
import SelectedTemplatePreview from '../TemplateStep/SelectedTemplatePreview';

import CampaignSummaryPanel from './CampaignSummaryPanel';
import SendTimePanel from './SendTimePanel';

import styles from './ReviewAndSendStep.module.scss';

/**
 * Step 3 of the campaign wizard — Review & Send.
 *
 * Layout (matches design captures):
 *   - Left column  → `SendTimePanel` (Send Now / Schedule + date+time when
 *                    scheduled). The CTA (`Send` or `Schedule`) lives in the
 *                    wizard header, NOT in this panel — the panel only owns
 *                    the time inputs.
 *   - Right column → read-only preview of the picked template (reuses
 *                    `SelectedTemplatePreview` from Step 2 without the Edit /
 *                    Change Template action links).
 *
 * The Confirm-and-charge UX lives in `SendConfirmationModal` — this step is
 * only responsible for the form inputs that feed into it.
 */
interface ReviewAndSendStepProps extends EmailCampaignStepProps {
    /**
     * Resolved recipient count post-exclusions — already computed by the
     * wizard for the modal; passed here so the panel doesn't recompute it
     * and risk drifting from the modal figure.
     */
    recipientTotal: number;
    /**
     * Opens the wizard-root EmailSender drawer. Wired from the sender-not-
     * ready alert card so the user can jump straight to sender configuration
     * without leaving the wizard.
     */
    onOpenSenderDrawer: () => void;
}

const ReviewAndSendStep: React.FC<ReviewAndSendStepProps> = ({
    values,
    onChange,
    recipientTotal,
    onOpenSenderDrawer
}) => {
    const templatesList = useSelector((state: RootState) => state.emailTemplates.list);
    const sender = useSelector((state: RootState) => state.emailMarketingSender.data);

    const selectedTemplate = React.useMemo<EmailTemplate | null>(() => {
        if (values.emailTemplateId == null) return null;
        return templatesList.find(template => template.id === values.emailTemplateId) ?? null;
    }, [values.emailTemplateId, templatesList]);

    // Pre-emptive alert on the review step: catches the mandatory-sender rule
    // before the user clicks Continue → opens the send modal (which surfaces
    // the same block). Copy diverges by whether the marketplace has opted in
    // at all vs opted in but not verified, matching `SendConfirmationModal`.
    const senderReady = isSenderReadyForSend(sender);

    return (
        <div className={styles.root}>
            <div className={styles.formColumn}>
                {!senderReady && (
                    <AlertMessage
                        icon={faInfoCircle}
                        iconColor="#BAA997"
                        bdColor="#BCB8AE"
                        bgColor="#F8F5F1"
                        className={styles.senderAlert}
                    >
                        <span>
                            {sender?.enabled ? (
                                <>
                                    Your custom sender isn&apos;t verified yet. Verify it before you can send this
                                    campaign.
                                </>
                            ) : (
                                <>You need to configure a verified sender before you can send this campaign.</>
                            )}{' '}
                            <button
                                type="button"
                                className={styles.senderLink}
                                onClick={onOpenSenderDrawer}
                            >
                                {sender?.enabled ? 'Manage sender' : 'Set up sender'}
                            </button>
                        </span>
                    </AlertMessage>
                )}
                <SendTimePanel
                    sendTime={values.sendTime}
                    scheduledDate={values.scheduledDate}
                    scheduledTime={values.scheduledTime}
                    onChangeSendTime={sendTime => onChange({ sendTime })}
                    onChangeScheduledDate={scheduledDate => onChange({ scheduledDate })}
                    onChangeScheduledTime={scheduledTime => onChange({ scheduledTime })}
                />
                <CampaignSummaryPanel
                    values={values}
                    recipientTotal={recipientTotal}
                />
            </div>

            <div className={styles.previewColumn}>
                {selectedTemplate ? (
                    <SelectedTemplatePreview template={selectedTemplate} />
                ) : (
                    <div className={styles.previewPlaceholder}>
                        No template selected. Go back to Step 2 to pick one.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewAndSendStep;
